/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useServiceContext } from '../../../root'
import { UserRole } from '../../../types/access'
import { roleConfigMap, StudyTableMode } from './types'
import { StudiesTable } from './StudiesTable'
import { SitesTable } from './SitesTable'
import { StudiesFilterPanel } from './StudiesFilterPanel'
import { SitesFilterPanel } from './SitesFilterPanel'
import FilterTrigger from '../../../components/shared/FilterTrigger'
import { RcmTableDataWrapper } from '../../../components/rcm-table/RcmTableDataWrapper'
import { useRcmTableData } from '../../../hooks/useRcmTableData'
import persistService from '../../../services/persist'
import { getMyStudies, getMyEnvironments, getMySites } from '../../../services/api/etmf'
import Summary from '~/pages/etmf/overview/Summary'

/**
 * Studies content component - must be inside RcmTableDataWrapper to access context
 */
const StudiesContent: React.FC<{ mode: StudyTableMode; userRole: UserRole; persistenceKey: string }> = ({
  mode,
  userRole,
  persistenceKey
}) => {
  const tableData = useRcmTableData()
  const prevFsiIdsRef = useRef<number[]>([])
  const [isCollapsed, setIsCollapsed] = useState(true)

  // Listen to persistence changes from FilterTrigger
  useEffect(() => {
    const checkPersistence = () => {
      const settings = persistService.getBasic<{ collapsed: boolean }>(persistenceKey)
      const collapsed = settings?.collapsed ?? true
      setIsCollapsed(collapsed)
      if (tableData?.setIsFilterCollapsed) {
        tableData.setIsFilterCollapsed(collapsed)
      }
    }

    // Initial check
    checkPersistence()

    // Poll for changes (simple approach)
    const interval = setInterval(checkPersistence, 100)
    return () => clearInterval(interval)
  }, [persistenceKey, tableData])

  const handleFilterChange = useCallback(
    (fsiIds: number[]) => {
      // Only apply filter if fsiIds actually changed to prevent infinite loops
      const prevFsiIds = prevFsiIdsRef.current
      const hasChanged = fsiIds.length !== prevFsiIds.length || fsiIds.some((id, idx) => id !== prevFsiIds[idx])

      if (hasChanged) {
        prevFsiIdsRef.current = fsiIds
        tableData.applyFilter({ fsiIds })
      }
    },
    [tableData.applyFilter]
  )

  return (
    <>
      <div className="no-transition">
        <StudiesFilterPanel mode={mode} onFilterChange={handleFilterChange} isCollapsed={isCollapsed} />
      </div>
      <StudiesTable mode={mode} userRole={userRole} />
    </>
  )
}

/**
 * Sites content component - must be inside RcmTableDataWrapper to access context
 */
const SitesContent: React.FC<{ userRole: UserRole; persistenceKey: string }> = ({ userRole, persistenceKey }) => {
  const tableData = useRcmTableData()
  const prevFsiIdsRef = useRef<number[]>([])
  const [isCollapsed, setIsCollapsed] = useState(true)

  // Listen to persistence changes from FilterTrigger
  useEffect(() => {
    const checkPersistence = () => {
      const settings = persistService.getBasic<{ collapsed: boolean }>(persistenceKey)
      const collapsed = settings?.collapsed ?? true
      setIsCollapsed(collapsed)
      if (tableData?.setIsFilterCollapsed) {
        tableData.setIsFilterCollapsed(collapsed)
      }
    }

    // Initial check
    checkPersistence()

    // Poll for changes (simple approach)
    const interval = setInterval(checkPersistence, 100)
    return () => clearInterval(interval)
  }, [persistenceKey, tableData])

  const handleFilterChange = useCallback(
    (fsiIds: number[]) => {
      // Only apply filter if fsiIds actually changed to prevent infinite loops
      const prevFsiIds = prevFsiIdsRef.current
      const hasChanged = fsiIds.length !== prevFsiIds.length || fsiIds.some((id, idx) => id !== prevFsiIds[idx])

      if (hasChanged) {
        prevFsiIdsRef.current = fsiIds
        tableData.applyFilter({ fsiIds })
      }
    },
    [tableData.applyFilter]
  )

  return (
    <>
      <div className="no-transition">
        <SitesFilterPanel onFilterChange={handleFilterChange} isCollapsed={tableData.isFilterCollapsed ?? true} />
      </div>
      <SitesTable userRole={userRole} />
    </>
  )
}

/**
 * Staging Overview Page - Modern TypeScript React implementation
 *
 * Replaces: lego/page/staging/overview/Page.jsx
 *
 * Key improvements:
 * - Single unified architecture (no duplicate table components)
 * - TypeScript type safety
 * - Modern React patterns (hooks, functional components)
 * - Simplified role-based rendering logic
 * - Proper state management and persistence
 * - Clean separation of concerns
 */
export const StagingOverviewPage: React.FC = () => {
  const { t } = useTranslation()
  const { service } = useServiceContext()

  // Get current user role
  const userRole = service.auth.getRole() as UserRole

  useEffect(() => {
    service.staging.exitEnvironment()
    service.staging.exitSite()
    service.staging.exitStudy()
    service.staging.exitPropertySearch()
  }, [])

  // Get configuration for current role
  const config = useMemo(() => {
    return roleConfigMap[userRole] || roleConfigMap[UserRole.USER]
  }, [userRole])

  // Load data functions for the wrappers
  const loadStudiesData = useCallback(
    async (params: any, filter: any = {}) => {
      const userId = service.auth.getUserId()
      if (!userId) {
        return { elements: [], totalElements: 0 }
      }

      const apiParams = {
        userId: userId.toString(),
        sortBy: params.sortBy || 'NAME',
        sortDesc: params.sortDesc || false,
        pageStart: params.pageStart || 0,
        pageSize: params.pageSize || 15,
        fsiIds: filter.fsiIds && filter.fsiIds.length > 0 ? filter.fsiIds : undefined
      }

      const response =
        config.studyTableMode === 'studies' ? await getMyStudies(apiParams) : await getMyEnvironments(apiParams)

      return {
        elements: response.elements,
        totalElements: response.totalElements
      }
    },
    [service.auth, config.studyTableMode]
  )

  const loadSitesData = useCallback(
    async (params: any, filter: any = {}) => {
      const userId = service.auth.getUserId()
      if (!userId) {
        return { elements: [], totalElements: 0 }
      }

      const apiParams = {
        userId: userId.toString(),
        sortBy: params.sortBy || 'NAME',
        sortDesc: params.sortDesc || false,
        pageStart: params.pageStart || 0,
        pageSize: params.pageSize || 15,
        fsiIds: filter.fsiIds && filter.fsiIds.length > 0 ? filter.fsiIds : undefined
      }

      const response = await getMySites(apiParams)

      return {
        elements: response.elements,
        totalElements: response.totalElements
      }
    },
    [service.auth]
  )

  // Don't render anything if role has no access
  if (!config.showStudies && !config.showSites) {
    return (
      <div className="alert alert-warning m-4">
        <h4>{t('ACCESS_DENIED')}</h4>
        <p>{t('NO_STAGING_ACCESS')}</p>
      </div>
    )
  }

  return (
    <>
      {config.userRole === UserRole.ETMF_SPECIALIST ||
      config.userRole === UserRole.ETMF_MANAGER ||
      config.userRole === UserRole.ETMF_SPONSOR ? (
        <Summary />
      ) : (
        <div className="staging-area-table">
          <div className={config.isFullWidth ? '' : 'd-flex flex-row align-items-baseline'}>
            {/* Studies/Environments Section */}
            {config.showStudies && (
              <div style={config.isFullWidth ? {} : { width: '50%' }}>
                <div className="mcc-content-frame theme-sandman" id="browser-wrapper-study">
                  <div className="sub-page-header">
                    <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
                      <h2 className={config.isFullWidth ? 'col-lg-12 d-contents' : 'col-lg-6 d-contents'}>
                        {t('DASHBOARD_MY_STUDIES')}
                      </h2>
                      <div className="align-right d-flex">
                        <FilterTrigger persistenceKey="myStudiesFilterTrigger" />
                      </div>
                    </div>
                  </div>

                  <div id="page-content">
                    {/* Studies Table wrapped in RcmTableDataWrapper */}
                    <RcmTableDataWrapper
                      loadData={loadStudiesData}
                      defaultSortBy="NAME"
                      defaultSortDesc={false}
                      persistenceKey="myStudiesTable"
                      doPreload={true}
                      reloadController={(refreshFn) => {
                        // Wrapper manages filter state internally
                      }}
                    >
                      <StudiesContent
                        mode={config.studyTableMode}
                        userRole={userRole}
                        persistenceKey="myStudiesFilterTrigger"
                      />
                    </RcmTableDataWrapper>
                  </div>
                </div>
              </div>
            )}

            {/* Sites Section */}
            {config.showSites && (
              <div style={config.isFullWidth ? {} : { width: '50%' }}>
                <div className="mcc-content-frame theme-sandman" id="browser-wrapper-site">
                  <div className="sub-page-header">
                    <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
                      <h2 className={config.isFullWidth ? 'col-lg-12 d-contents' : 'col-lg-6 d-contents'}>
                        {t('DASHBOARD_MY_SITES')}
                      </h2>
                      <div className="align-right d-flex">
                        <FilterTrigger persistenceKey="mySitesFilterTrigger" />
                      </div>
                    </div>
                  </div>

                  <div id="page-content">
                    {/* Sites Table wrapped in RcmTableDataWrapper */}
                    <RcmTableDataWrapper
                      loadData={loadSitesData}
                      defaultSortBy="NAME"
                      defaultSortDesc={false}
                      persistenceKey="mySitesTable"
                      doPreload={true}
                      reloadController={(refreshFn) => {
                        // Wrapper manages filter state internally
                      }}
                    >
                      <SitesContent userRole={userRole} persistenceKey="mySitesFilterTrigger" />
                    </RcmTableDataWrapper>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  )
}

export default StagingOverviewPage
